import React, {Fragment, useRef, useState} from 'react';
import BlockTitle from "../_BlockTitle";
import classNames from 'classnames';
import styles from './styles/HTMLBlock.module.scss';
import {observer} from 'mobx-react';
import {runInAction} from 'mobx';
import WatsonApi from "../../../../backends/WatsonApi";
import NotificationManager from "../../../notifications/NotificationManager";
import {EditDropDownItem} from "../EditDropDown";
import FontAwesome from "../../../utilities/FontAwesome";
import SaveCancelButtons from "../SaveCancelButtons";
import {useStores} from "../../../../stores/useStores";
import {ErrorBoundary} from "../../../utilities/rollbar/ErrorBoundary";
import DangerouslySetHtmlContent from "../../../utilities/DangerouslySetHtmlContent";
import {HtmlEditor, serializeSlateToHtml} from "../../../editors/HtmlEditor";
import type {SlateEditor} from "platejs";
import {NodeType} from "../../../editors/utils";
import {PlaceholderPlugin} from "@platejs/media/react";
import {compressImage} from "../../../../utils/DataUtilities";
import {getCdnUrl} from "../../../../utils/SchoolBlocksUtilities";
import {useFeatureFlags} from "../../../../utils/featureFlags/useFeatureFlags";

const htmlBlockContainerClassName = classNames({
    [styles.htmlBlockContainer]: true,
});

const HtmlTerrace = observer((props: {
    blockObj: IHTmlTerrace,
    aboveTheFold: boolean,
}) => {
    const {gridStore} = useStores();
    const {html_terrace_html_editor} = useFeatureFlags();

    const htmlEditorRef = useRef<{ editor: SlateEditor }>(null);
    const [code, setCode] = useState(props.blockObj.json_data?.html?.code || "");
    const [editMode, setEditMode] = useState(false);

    const handleSaveHtmlEditor = async () => {
        try {
            const editor = htmlEditorRef.current?.editor;
            if (!editor) return;

            const client = await WatsonApi();
            const mediaPlaceholders = editor.api.nodes({
                match: {type: NodeType.MEDIA_PLACEHOLDER},
                at: [],
            });
            for (let placeholder of mediaPlaceholders) {
                const placeholderApi = editor.getApi(PlaceholderPlugin);
                const placeholderId = placeholder[0].id as string;
                const file = placeholderApi.placeholder.getUploadingFile(placeholderId);
                const compressedFile = await compressImage(file, 2);

                const response = await client.apis.organizations.organizations_terraces_upload_image({
                    organization_pk: props.blockObj.organization_id,
                    id: props.blockObj.id,
                    file: compressedFile,
                });
                const imageUrl = response.body.url;
                editor.tf.setNodes({
                    type: NodeType.IMAGE,
                    url: getCdnUrl(imageUrl),
                }, {
                    at: placeholder[1],
                })
            }

            const html = await serializeSlateToHtml(editor);
            const response = await client.apis.organizations.organizations_terraces_partial_update({
                organization_pk: props.blockObj.organization_id,
                id: props.blockObj.id,
                data: {
                    json_data: {
                        html: {
                            code: html,
                        },
                    },
                },
            })
            setEditMode(false);
            setCode(html);
            runInAction(() => {
                const updatedTerrace = JSON.parse(response.data);
                gridStore.blocks.set(updatedTerrace.id, updatedTerrace);
            });
            NotificationManager.success("Terrace successfully saved!");
        } catch (e) {
            setCode(props.blockObj.json_data?.html?.code || "");
            NotificationManager.error("Something went wrong! Please try again.");
        }
    }

    const handleSaveRawEditor = async () => {
        try {
            const client = await WatsonApi();
            const response = await client.apis.organizations.organizations_terraces_partial_update({
                organization_pk: props.blockObj.organization_id,
                id: props.blockObj.id,
                data: {
                    json_data: {
                        html: {
                            code,
                        },
                    },
                },
            })
            setEditMode(false);
            runInAction(() => {
                const updatedTerrace = JSON.parse(response.data);
                gridStore.blocks.set(updatedTerrace.id, updatedTerrace);
            });
            NotificationManager.success("Terrace successfully saved!");
        } catch (e) {
            setCode(props.blockObj.json_data?.html?.code || "");
            NotificationManager.error("Something went wrong! Please try again.");
        }
    }

    const _setEditMode = async (value, cancel) => {
        if (value) {
            setEditMode(value);
        } else if (!cancel) {
            if (html_terrace_html_editor) {
                await handleSaveHtmlEditor();
            } else {
                await handleSaveRawEditor();
            }
        }
        else if (cancel) {
            setCode(props.blockObj.json_data?.html?.code || "");
            setEditMode(false);
        }
    }

    const htmlTerraceDropDownItems: EditDropDownItem[] = [
        {
            title: 'Edit HTML',
            icon: <FontAwesome prefix={'fas'} fixedWidth className="fa-pencil-alt"/>,
            onClick: () => _setEditMode(!editMode, true),
        },
    ]

    let editor;
    if (html_terrace_html_editor) {
        editor = <HtmlEditor editorClassName={styles.editor} value={code} defaultEditor={"html"} ref={htmlEditorRef} />
    } else {
        editor = <>
            <div>
                <p>Enter Custom HTML below:</p>
            </div>
            <textarea onChange={e => setCode(e.target.value)} value={code}/>
        </>
    }

    return (
        <article className={htmlBlockContainerClassName}>
            <BlockTitle
                blockObj={props.blockObj}
                editing={editMode}
                setEditing={_setEditMode}
                aboveTheFold={props.aboveTheFold}
                dropDownItems={htmlTerraceDropDownItems}
                allowSave={true}
                showViewMore={false}
            />
            {editMode ? <>
                    {editor}
                    <SaveCancelButtons editMode={editMode} setEditing={_setEditMode}/>
                </> :
                <ErrorBoundary userGenerated={true}>
                    <div className={html_terrace_html_editor ? "scoped-content-builder" : ""}>
                        <DangerouslySetHtmlContent html={code}/>
                    </div>
                </ErrorBoundary>}
        </article>
    );
})

export default HtmlTerrace;
